import { useState, useEffect } from "react";
import { InsightsNavbar } from "@/components/InsightsNavbar";
import { AnimatedSection } from "@/components/AnimatedSection";
import { Footer } from "@/components/Footer";
import { ScrollProgressIndicator } from "@/components/ScrollProgressIndicator";
import { ToolAccessGate } from "@/components/ToolAccessGate";
import { IssueReportModal } from "@/components/IssueReportModal";
import { GmailPromotionsAnalyzer } from "@/components/insights/gmail-promotions/GmailPromotionsAnalyzer";
import { SubjectLineAnalyzer } from "@/components/tools/SubjectLineAnalyzer";
import { LiveStatsCounter } from "@/components/LiveStatsCounter";
import { EmailAuthChecker } from "@/components/tools/EmailAuthChecker";
import { BlacklistChecker } from "@/components/tools/BlacklistChecker";
import { EmailPreviewTool } from "@/components/tools/EmailPreviewTool";
import { EmailHeaderAnalyzer } from "@/components/tools/EmailHeaderAnalyzer";
import { EmailContentAnalyzer } from "@/components/tools/EmailContentAnalyzer";
import { EmailChecker } from "@/components/tools/EmailChecker";
import { SeoMetaAnalyzer } from "@/components/tools/SeoMetaAnalyzer";
import { PhishingDetector } from "@/components/tools/PhishingDetector";
import { RobotsMetaChecker } from "@/components/tools/RobotsMetaChecker";
import { DomainAgeChecker } from "@/components/tools/DomainAgeChecker";
import { LinkValidator } from "@/components/tools/LinkValidator";
import { DmarcReportAnalyzer } from "@/components/tools/DmarcReportAnalyzer";
import { M365TenantAnalyzer } from "@/components/tools/M365TenantAnalyzer";
import { BulkSubjectLineAnalyzer } from "@/components/tools/BulkSubjectLineAnalyzer";
import { DnsPropagationChecker } from "@/components/tools/DnsPropagationChecker";
import { PMCostCalculator } from "@/components/tools/PMCostCalculator";
import { MeetingCostCalculator } from "@/components/tools/MeetingCostCalculator";
import { CollaborationComparison } from "@/components/tools/CollaborationComparison";
import { GlobalLatencyTester } from "@/components/tools/GlobalLatencyTester";
import { WebhookDebugger } from "@/components/tools/WebhookDebugger";
import { ApiResponseSimulator } from "@/components/tools/ApiResponseSimulator";
import { BounceRateCalculator } from "@/components/tools/BounceRateCalculator";
import { SEO, createToolSchema } from "@/components/SEO";
import { analytics } from "@/hooks/useAnalytics";
import {
  Sparkles, 
  Mail, 
  Inbox, 
  ArrowRight, 
  Shield, 
  Ban, 
  Eye, 
  FileText, 
  X,
  Filter,
  Accessibility,
  Zap,
  Lock,
  BarChart3,
  Layers,
  MousePointerClick,
  AlertTriangle,
  Search,
  ShieldAlert,
  Bot,
  Calendar,
  Link2,
  PieChart,
  Building2,
  Globe,
  FolderKanban,
  Clock,
  Users2,
  Webhook,
  Server,
  Gauge,
  ExternalLink
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

interface Tool {
  id: string;
  title: string;
  description: string;
  icon: any;
  gradient: string;
  iconBg: string;
  category: string;
  badge?: string;
  component: React.ReactNode;
}

interface ToolCategory {
  id: string;
  title: string;
  description: string;
  icon: any;
}

const categories: ToolCategory[] = [
  {
    id: "authentication",
    title: "Authentication & Security",
    description: "Verify your email infrastructure",
    icon: Lock,
  },
  {
    id: "deliverability",
    title: "Deliverability Analysis",
    description: "Diagnose and optimize inbox placement",
    icon: BarChart3,
  },
  {
    id: "content",
    title: "Content Optimization",
    description: "Perfect your email content",
    icon: Layers,
  },
  {
    id: "seo",
    title: "SEO Tools",
    description: "Optimize for search engines",
    icon: Search,
  },
  {
    id: "phishing",
    title: "Phishing Detection",
    description: "Identify and prevent email threats",
    icon: ShieldAlert,
  },
  {
    id: "productivity",
    title: "Productivity Tools",
    description: "Compare and optimize your workflow",
    icon: FolderKanban,
  },
  {
    id: "developer",
    title: "Developer Tools",
    description: "Debug and test your integrations",
    icon: Server,
  },
];

const Tools = () => {
  const [activeTool, setActiveTool] = useState<string | null>(null);
  const [issueModalOpen, setIssueModalOpen] = useState(false);
  const [toolCount] = useState(24);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState<string>("all");

  const toolsList: Tool[] = [
    {
      id: "email-checker",
      title: "Email Deliverability Tester",
      description: "Send a test email and get a comprehensive deliverability report",
      icon: Mail,
      gradient: "from-primary to-purple-600",
      iconBg: "bg-gradient-to-br from-primary/20 to-purple-600/20",
      category: "deliverability",
      badge: "New",
      component: <EmailChecker />,
    },
    {
      id: "auth-checker",
      title: "SPF/DKIM/DMARC Checker",
      description: "Verify email authentication DNS records instantly",
      icon: Shield,
      gradient: "from-emerald-500 to-teal-600",
      iconBg: "bg-gradient-to-br from-emerald-500/20 to-teal-600/20",
      category: "authentication",
      badge: "Popular",
      component: <EmailAuthChecker />,
    },
    {
      id: "blacklist-checker",
      title: "IP & Domain Blacklist Scanner",
      description: "Check major email blocklists instantly",
      icon: Ban,
      gradient: "from-red-500 to-rose-600",
      iconBg: "bg-gradient-to-br from-red-500/20 to-rose-600/20",
      category: "authentication",
      component: <BlacklistChecker />,
    },
    {
      id: "header-analyzer",
      title: "Email Header Decoder",
      description: "Debug deliverability with AI header analysis",
      icon: FileText,
      gradient: "from-amber-500 to-yellow-600",
      iconBg: "bg-gradient-to-br from-amber-500/20 to-amber-600/20",
      category: "deliverability",
      badge: "AI Powered",
      component: <EmailHeaderAnalyzer />,
    },
    {
      id: "tab-predictor",
      title: "Gmail Tab Predictor",
      description: "Predict Primary vs Promotions placement",
      icon: Inbox,
      gradient: "from-blue-500 to-indigo-600",
      iconBg: "bg-gradient-to-br from-blue-500/20 to-indigo-600/20",
      category: "deliverability",
      badge: "AI Powered",
      component: <GmailPromotionsAnalyzer />,
    },
    {
      id: "content-analyzer",
      title: "Accessibility & Mobile Checker",
      description: "AI-powered accessibility and mobile analysis",
      icon: Accessibility,
      gradient: "from-indigo-500 to-purple-600",
      iconBg: "bg-gradient-to-br from-indigo-500/20 to-purple-600/20",
      category: "content",
      badge: "AI Powered",
      component: <EmailContentAnalyzer />,
    },
    {
      id: "email-preview",
      title: "Email Client Preview",
      description: "See rendering across Gmail, Outlook, Apple Mail",
      icon: Eye,
      gradient: "from-cyan-500 to-blue-600",
      iconBg: "bg-gradient-to-br from-cyan-500/20 to-blue-600/20",
      category: "content",
      component: <EmailPreviewTool />,
    },
    {
      id: "subject-analyzer",
      title: "Spam Trigger Analyzer",
      description: "AI detection of spam triggers in subject lines or email body",
      icon: Mail,
      gradient: "from-purple-500 to-pink-600",
      iconBg: "bg-gradient-to-br from-purple-500/20 to-pink-600/20",
      category: "content",
      badge: "AI Powered",
      component: <SubjectLineAnalyzer />,
    },
    {
      id: "bulk-subject-analyzer",
      title: "Bulk Subject Line Analyzer",
      description: "Compare multiple subject lines and find the best performer",
      icon: Layers,
      gradient: "from-violet-500 to-fuchsia-600",
      iconBg: "bg-gradient-to-br from-violet-500/20 to-fuchsia-600/20",
      category: "content",
      badge: "AI Powered",
      component: <BulkSubjectLineAnalyzer />,
    },
    {
      id: "seo-meta-analyzer",
      title: "SEO Meta Tag Analyzer",
      description: "Optimize page titles and descriptions for search",
      icon: Search,
      gradient: "from-green-500 to-emerald-600",
      iconBg: "bg-gradient-to-br from-green-500/20 to-emerald-600/20",
      category: "seo",
      badge: "New",
      component: <SeoMetaAnalyzer />,
    },
    {
      id: "robots-checker",
      title: "Robots Meta Checker",
      description: "Analyze robots directives and their SEO impact",
      icon: Bot,
      gradient: "from-slate-500 to-zinc-600",
      iconBg: "bg-gradient-to-br from-slate-500/20 to-zinc-600/20",
      category: "seo",
      component: <RobotsMetaChecker />,
    },
    {
      id: "phishing-detector",
      title: "Phishing Email Detector",
      description: "Analyze emails for phishing indicators and scams",
      icon: ShieldAlert,
      gradient: "from-red-600 to-orange-600",
      iconBg: "bg-gradient-to-br from-red-600/20 to-orange-600/20",
      category: "phishing",
      badge: "Security",
      component: <PhishingDetector />,
    },
    {
      id: "domain-age-checker",
      title: "Domain Age Checker",
      description: "Look up domain registration date via WHOIS",
      icon: Calendar,
      gradient: "from-teal-500 to-cyan-600",
      iconBg: "bg-gradient-to-br from-teal-500/20 to-cyan-600/20",
      category: "authentication",
      badge: "New",
      component: <DomainAgeChecker />,
    },
    {
      id: "link-validator",
      title: "Link Validator",
      description: "Check URLs for safety, redirects, and blocklists",
      icon: Link2,
      gradient: "from-orange-500 to-amber-600",
      iconBg: "bg-gradient-to-br from-orange-500/20 to-amber-600/20",
      category: "phishing",
      badge: "New",
      component: <LinkValidator />,
    },
    {
      id: "dmarc-analyzer",
      title: "DMARC Report Analyzer",
      description: "Parse and visualize DMARC aggregate reports",
      icon: PieChart,
      gradient: "from-fuchsia-500 to-pink-600",
      iconBg: "bg-gradient-to-br from-fuchsia-500/20 to-pink-600/20",
      category: "authentication",
      badge: "New",
      component: <DmarcReportAnalyzer />,
    },
    {
      id: "m365-tenant-analyzer",
      title: "Google/Microsoft 365 Analyzer",
      description: "Find tenant info, all domains, and DNS records",
      icon: Building2,
      gradient: "from-blue-600 to-indigo-600",
      iconBg: "bg-gradient-to-br from-blue-600/20 to-indigo-600/20",
      category: "authentication",
      badge: "New",
      component: <M365TenantAnalyzer />,
    },
    {
      id: "dns-propagation-checker",
      title: "DNS Propagation Checker",
      description: "Check if DNS records have propagated globally",
      icon: Globe,
      gradient: "from-sky-500 to-blue-600",
      iconBg: "bg-gradient-to-br from-sky-500/20 to-blue-600/20",
      category: "authentication",
      badge: "New",
      component: <DnsPropagationChecker />,
    },
    {
      id: "pm-cost-calculator",
      title: "PM Tool Cost Calculator",
      description: "Compare project management tool costs by team size",
      icon: FolderKanban,
      gradient: "from-violet-500 to-purple-600",
      iconBg: "bg-gradient-to-br from-violet-500/20 to-purple-600/20",
      category: "productivity",
      badge: "New",
      component: <PMCostCalculator />,
    },
    {
      id: "meeting-cost-calculator",
      title: "Meeting Cost Calculator",
      description: "Calculate the true cost of meetings to your organization",
      icon: Clock,
      gradient: "from-orange-500 to-red-600",
      iconBg: "bg-gradient-to-br from-orange-500/20 to-red-600/20",
      category: "productivity",
      badge: "New",
      component: <MeetingCostCalculator />,
    },
    {
      id: "collaboration-comparison",
      title: "Team Collaboration Comparison",
      description: "Compare Slack, Teams, Notion & 8+ collaboration tools",
      icon: Users2,
      gradient: "from-cyan-500 to-blue-600",
      iconBg: "bg-gradient-to-br from-cyan-500/20 to-blue-600/20",
      category: "productivity",
      badge: "New",
      component: <CollaborationComparison />,
    },
    {
      id: "global-latency-tester",
      title: "Global Latency Tester",
      description: "Check website load speed from 8 global locations",
      icon: Gauge,
      gradient: "from-emerald-500 to-teal-600",
      iconBg: "bg-gradient-to-br from-emerald-500/20 to-teal-600/20",
      category: "developer",
      badge: "New",
      component: <GlobalLatencyTester />,
    },
    {
      id: "webhook-debugger",
      title: "Webhook Debugger",
      description: "Temporary URL to catch and inspect incoming webhooks",
      icon: Webhook,
      gradient: "from-purple-500 to-indigo-600",
      iconBg: "bg-gradient-to-br from-purple-500/20 to-indigo-600/20",
      category: "developer",
      badge: "New",
      component: <WebhookDebugger />,
    },
    {
      id: "api-response-simulator",
      title: "API Response Simulator",
      description: "Mock API responses for frontend testing",
      icon: Server,
      gradient: "from-rose-500 to-pink-600",
      iconBg: "bg-gradient-to-br from-rose-500/20 to-pink-600/20",
      category: "developer",
      badge: "New",
      component: <ApiResponseSimulator />,
    },
    {
      id: "bounce-rate-calculator",
      title: "Bounce Rate Calculator",
      description: "Estimate email bounce rates based on list age, verification, and engagement",
      icon: BarChart3,
      gradient: "from-amber-500 to-orange-600",
      iconBg: "bg-gradient-to-br from-amber-500/20 to-orange-600/20",
      category: "deliverability",
      badge: "New",
      component: <BounceRateCalculator />,
    },
  ];

  const openTool = (id: string) => {
    const tool = toolsList.find(t => t.id === id);
    if (tool) {
      analytics.toolOpened(id, tool.title);
    }
    setActiveTool(id);
    document.body.style.overflow = "hidden";
  };

  const closeTool = () => {
    setActiveTool(null);
    document.body.style.overflow = "auto";
  };

  // Ensure scroll is restored on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  // Check for tool to open from session storage (from Home page navigation)
  useEffect(() => {
    const toolToOpen = sessionStorage.getItem('openTool');
    if (toolToOpen) {
      sessionStorage.removeItem('openTool');
      // Small delay to ensure page is ready
      setTimeout(() => openTool(toolToOpen), 100);
    }
  }, []);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeTool();
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, []);

  const activeToolData = toolsList.find((t) => t.id === activeTool);

  // Filter tools based on search and category
  const filteredTools = toolsList.filter((tool) => {
    const matchesSearch = searchQuery.trim() === "" || 
      tool.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tool.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = activeCategory === "all" || tool.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  // Get categories that have matching tools
  const categoriesWithTools = categories.filter(cat => 
    filteredTools.some(tool => tool.category === cat.id)
  );

  // Tool schemas for structured data
  const toolSchemas = [
    createToolSchema({ name: "SPF/DKIM/DMARC Checker", description: "Verify email authentication DNS records instantly", category: "Email Security" }),
    createToolSchema({ name: "Gmail Tab Predictor", description: "Predict Primary vs Promotions tab placement", category: "Email Deliverability" }),
    createToolSchema({ name: "Email Header Analyzer", description: "Debug deliverability with AI header analysis", category: "Email Analysis" }),
    createToolSchema({ name: "PM Tool Cost Calculator", description: "Compare project management tool costs by team size", category: "Productivity" }),
    createToolSchema({ name: "Meeting Cost Calculator", description: "Calculate the true cost of meetings to your organization", category: "Productivity" }),
    createToolSchema({ name: "Team Collaboration Comparison", description: "Compare Slack, Teams, Notion & 8+ collaboration tools", category: "Productivity" }),
  ];

  return (
    <>
      <SEO
        title="Free Email Deliverability Tools"
        description="24 free AI-powered tools for email deliverability, security analysis, SEO, and productivity. Check SPF/DKIM/DMARC, compare PM tools, calculate meeting costs, and more. No signup required."
        canonical="/tools"
        keywords={["email tools", "SPF checker", "DKIM checker", "DMARC checker", "email deliverability", "blacklist checker", "PM tool comparison", "meeting cost calculator", "collaboration tools"]}
        jsonLd={toolSchemas}
      />
      <InsightsNavbar />
      <ScrollProgressIndicator />
      <IssueReportModal 
        isOpen={issueModalOpen} 
        onClose={() => setIssueModalOpen(false)}
        toolId={activeTool || undefined}
        toolName={activeToolData?.title}
      />
      
      <main className="min-h-screen">
        {/* Premium Hero Section */}
        <section className="relative overflow-hidden">
          {/* Background Effects */}
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background to-muted/30" />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-primary/5 rounded-full blur-3xl" />
          <div className="absolute top-20 right-0 w-[400px] h-[400px] bg-purple-500/5 rounded-full blur-3xl" />
          <div className="absolute top-40 left-0 w-[300px] h-[300px] bg-cyan-500/5 rounded-full blur-3xl" />
          
          {/* Grid Pattern Overlay */}
          <div 
            className="absolute inset-0 opacity-[0.015]"
            style={{
              backgroundImage: `linear-gradient(hsl(var(--foreground)) 1px, transparent 1px),
                               linear-gradient(90deg, hsl(var(--foreground)) 1px, transparent 1px)`,
              backgroundSize: '60px 60px'
            }}
          />

          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-20 sm:pt-24 pb-10 sm:pb-12">
            <AnimatedSection delay={0} className="text-center">
              {/* Premium Badge */}
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-primary/10 via-purple-500/10 to-cyan-500/10 border border-primary/20 mb-5">
                <Sparkles className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium bg-gradient-to-r from-primary via-purple-500 to-cyan-500 bg-clip-text text-transparent">
                  {toolCount} Free AI-Powered Tools
                </span>
              </div>

              {/* Main Heading */}
              <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-4">
                <span className="text-foreground">IT Dukes</span>
                <br />
                <span className="bg-gradient-to-r from-primary via-purple-500 to-cyan-500 bg-clip-text text-transparent">
                  Toolkit
                </span>
              </h1>

              <p className="text-muted-foreground text-base sm:text-lg max-w-2xl mx-auto leading-relaxed mb-8">
                Professional-grade AI tools for email deliverability, security analysis, and development. No signup required.
              </p>

              {/* Stats Row */}
              <div className="flex flex-wrap justify-center gap-6 sm:gap-10 mb-8">
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-foreground">{toolCount}</div>
                  <div className="text-xs sm:text-sm text-muted-foreground">Free Tools</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-foreground">5</div>
                  <div className="text-xs sm:text-sm text-muted-foreground">AI Powered</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-foreground">100%</div>
                  <div className="text-xs sm:text-sm text-muted-foreground">Free Forever</div>
                </div>
              </div>

              {/* Scroll Indicator */}
              <div className="flex flex-col items-center gap-1.5 text-muted-foreground animate-bounce">
                <MousePointerClick className="w-4 h-4" />
                <span className="text-xs">Click any tool to launch</span>
              </div>
            </AnimatedSection>
          </div>
        </section>

        {/* Search & Filter Bar */}
        <section className="sticky top-16 z-30 bg-background/95 backdrop-blur-sm border-b border-border py-4 -mt-2">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            {/* Search Input - Full width */}
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              <input
                type="text"
                placeholder="Search tools..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full max-w-lg pl-10 pr-10 py-2.5 rounded-xl bg-card border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/30 transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  style={{ right: searchQuery ? '12px' : undefined }}
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Category Filter - Scrollable on mobile */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setActiveCategory("all")}
                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                  activeCategory === "all"
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "bg-card border border-border text-muted-foreground hover:text-foreground hover:border-primary/30"
                }`}
              >
                <Filter className="w-3.5 h-3.5" />
                All
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                    activeCategory === cat.id
                      ? "bg-primary text-primary-foreground shadow-sm"
                      : "bg-card border border-border text-muted-foreground hover:text-foreground hover:border-primary/30"
                  }`}
                >
                  <cat.icon className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">{cat.title}</span>
                </button>
              ))}
            </div>

            {/* Results count */}
            {(searchQuery || activeCategory !== "all") && (
              <div className="mt-3 text-sm text-muted-foreground">
                Showing {filteredTools.length} of {toolsList.length} tools
                {searchQuery && <span> matching "{searchQuery}"</span>}
              </div>
            )}
          </div>
        </section>

        {/* Tools Grid by Category */}
        <section className="relative py-8 sm:py-12">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            {filteredTools.length === 0 ? (
              <div className="text-center py-16">
                <Search className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">No tools found</h3>
                <p className="text-muted-foreground mb-4">Try adjusting your search or filter</p>
                <button
                  onClick={() => { setSearchQuery(""); setActiveCategory("all"); }}
                  className="text-sm text-primary hover:underline"
                >
                  Clear all filters
                </button>
              </div>
            ) : (
              categoriesWithTools.map((category, categoryIndex) => {
                const categoryTools = filteredTools.filter(tool => tool.category === category.id);
                if (categoryTools.length === 0) return null;
                
                return (
                  <AnimatedSection key={category.id} delay={50 + categoryIndex * 30} className="mb-10 last:mb-0">
                    {/* Category Header */}
                    <div className="flex items-center gap-3 mb-5">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center border border-primary/10">
                        <category.icon className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h2 className="font-display text-lg sm:text-xl font-bold text-foreground">
                          {category.title}
                        </h2>
                        <p className="text-xs sm:text-sm text-muted-foreground">{category.description}</p>
                      </div>
                    </div>

                    {/* Tools Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {categoryTools.map((tool) => (
                        <button
                          key={tool.id}
                          onClick={() => openTool(tool.id)}
                          className="group relative bg-card hover:bg-card/80 border border-border hover:border-primary/30 rounded-xl p-5 text-left transition-all duration-300 hover:shadow-lg hover:shadow-primary/5 hover:-translate-y-0.5"
                        >
                          {/* Badge */}
                          {tool.badge && (
                            <div className="absolute top-3 right-3">
                              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-medium uppercase tracking-wider ${
                                tool.badge === "AI Powered" 
                                  ? "bg-gradient-to-r from-purple-500/10 to-pink-500/10 text-purple-600 border border-purple-500/20" 
                                  : "bg-primary/10 text-primary border border-primary/20"
                              }`}>
                                {tool.badge === "AI Powered" && <Zap className="w-2 h-2" />}
                                {tool.badge}
                              </span>
                            </div>
                          )}

                          {/* Icon */}
                          <div className={`w-11 h-11 rounded-xl ${tool.iconBg} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300`}>
                            <tool.icon className={`w-5 h-5 bg-gradient-to-br ${tool.gradient} bg-clip-text text-transparent`} style={{ color: `hsl(var(--primary))` }} />
                          </div>

                          {/* Content */}
                          <h3 className="font-semibold text-foreground text-base mb-1.5 group-hover:text-primary transition-colors">
                            {tool.title}
                          </h3>
                          <p className="text-xs text-muted-foreground leading-relaxed">
                            {tool.description}
                          </p>

                          {/* Launch Indicator */}
                          <div className="mt-3 flex items-center gap-1.5 text-xs font-medium text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                            <span>Launch Tool</span>
                            <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                          </div>

                          {/* Hover Glow Effect */}
                          <div className={`absolute inset-0 rounded-xl bg-gradient-to-br ${tool.gradient} opacity-0 group-hover:opacity-[0.03] transition-opacity pointer-events-none`} />
                        </button>
                      ))}
                    </div>
                  </AnimatedSection>
                );
              })
            )}
          </div>
        </section>

        {/* CTA Section */}
        <AnimatedSection delay={300}>
          <section className="relative py-16 sm:py-20 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-purple-500/5 to-cyan-500/5" />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/50 to-background" />
            
            <div className="relative max-w-4xl mx-auto px-4 sm:px-6 text-center">
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-foreground mb-4">
                Want Data-Driven Insights?
              </h2>
              <p className="text-muted-foreground max-w-xl mx-auto mb-8">
                Explore our research articles for actionable strategies backed by real deliverability data.
              </p>
              <Link 
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors shadow-lg shadow-primary/20"
              >
                Browse Insights
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </section>
        </AnimatedSection>
      </main>

      <Footer />

      {/* Tool Modal */}
      {activeTool && activeToolData && (
        <div 
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto"
          onClick={closeTool}
        >
          {/* Backdrop */}
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm" />
          
          {/* Modal */}
          <div 
            className="relative w-full max-w-4xl mx-4 my-8 sm:my-16"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-card border border-border rounded-2xl shadow-2xl overflow-hidden">
              {/* Modal Header */}
              <div className="sticky top-0 z-10 bg-card/95 backdrop-blur-sm border-b border-border px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-xl ${activeToolData.iconBg} flex items-center justify-center`}>
                    <activeToolData.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-foreground">{activeToolData.title}</h2>
                    <p className="text-sm text-muted-foreground">{activeToolData.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Link
                    to={`/tools/${activeTool}`}
                    className="w-10 h-10 rounded-xl hover:bg-muted flex items-center justify-center transition-colors"
                    title="Open in new page (shareable link)"
                  >
                    <ExternalLink className="w-5 h-5 text-muted-foreground" />
                  </Link>
                  <button
                    onClick={() => setIssueModalOpen(true)}
                    className="w-10 h-10 rounded-xl hover:bg-muted flex items-center justify-center transition-colors"
                    title="Report an issue"
                  >
                    <AlertTriangle className="w-5 h-5 text-muted-foreground" />
                  </button>
                  <button
                    onClick={closeTool}
                    className="w-10 h-10 rounded-xl bg-muted hover:bg-muted/80 flex items-center justify-center transition-colors"
                  >
                    <X className="w-5 h-5 text-muted-foreground" />
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6">
                <ToolAccessGate toolId={activeToolData.id} toolName={activeToolData.title}>
                  {({ userEmail, userName, sessionToken }) => {
                    const ToolComponent = toolsList.find(t => t.id === activeTool)?.component;
                    if (!ToolComponent) return null;
                    
                    // Clone component with user props
                    const toolProps = { userEmail, userName, sessionToken };
                    
                    switch (activeTool) {
                      case "subject-analyzer":
                        return <SubjectLineAnalyzer {...toolProps} />;
                      case "auth-checker":
                        return <EmailAuthChecker {...toolProps} />;
                      case "blacklist-checker":
                        return <BlacklistChecker {...toolProps} />;
                      case "header-analyzer":
                        return <EmailHeaderAnalyzer {...toolProps} />;
                      case "content-analyzer":
                        return <EmailContentAnalyzer {...toolProps} />;
                      default:
                        return ToolComponent;
                    }
                  }}
                </ToolAccessGate>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Live Stats Counter */}
      {!activeTool && <LiveStatsCounter />}
    </>
  );
};

export default Tools;
